import { useState, type FormEvent } from 'react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { TagsInput } from '@/components/tags-input';
import { Textarea } from '@/components/textarea';
import { useFormError } from '@/lib/form-error';
import { formatStatus } from '@/lib/format';
import {
  LEAD_SOURCES,
  LEAD_STAGES,
  type LeadInput,
  type LeadSource,
  type LeadStage,
} from '@/lib/leads';
import type { LeadData } from '@/types/api';

type Props = {
  initial?: Partial<LeadData>;
  onSubmit: (input: LeadInput) => Promise<unknown>;
  submitLabel: string;
};

export function LeadForm({ initial = {}, onSubmit, submitLabel }: Props) {
  const [form, setForm] = useState<LeadInput>({
    name: initial.name ?? '',
    email: initial.email ?? '',
    phone: initial.phone ?? '',
    company_name: initial.company_name ?? '',
    source: (initial.source as LeadSource | undefined) ?? 'manual',
    stage: (initial.stage as LeadStage | undefined) ?? 'new',
    value_estimate_cents: initial.value_estimate_cents ?? null,
    notes: initial.notes ?? '',
    lost_reason: initial.lost_reason ?? '',
  });
  const [tags, setTags] = useState<string[]>(initial.tags ?? []);
  const [pending, setPending] = useState(false);
  const { error, wrap } = useFormError();

  function update<K extends keyof LeadInput>(key: K, value: LeadInput[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    await wrap(() => onSubmit({ ...form, tags }));
    setPending(false);
  }

  const isLost = form.stage === 'lost';

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name">
          <Input
            required
            value={form.name ?? ''}
            onChange={(e) => update('name', e.target.value)}
            placeholder="Jane Smith"
          />
        </Field>
        <Field label="Company">
          <Input
            value={form.company_name ?? ''}
            onChange={(e) => update('company_name', e.target.value)}
            placeholder="Acme Co"
          />
        </Field>
        <Field label="Email">
          <Input
            type="email"
            value={form.email ?? ''}
            onChange={(e) => update('email', e.target.value)}
          />
        </Field>
        <Field label="Phone">
          <Input
            value={form.phone ?? ''}
            onChange={(e) => update('phone', e.target.value)}
            placeholder="+1 509 555 0123"
          />
        </Field>
        <Field label="Source">
          <Select
            value={form.source ?? 'manual'}
            onChange={(e) => update('source', e.target.value as LeadSource)}
          >
            {LEAD_SOURCES.map((s) => (
              <option key={s} value={s}>
                {s === 'web_form' ? 'Website' : formatStatus(s)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Stage">
          <Select
            value={form.stage ?? 'new'}
            onChange={(e) => update('stage', e.target.value as LeadStage)}
          >
            {LEAD_STAGES.map((s) => (
              <option key={s} value={s}>
                {formatStatus(s)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Value estimate ($)" className="col-span-2">
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="2500.00"
            value={
              form.value_estimate_cents != null
                ? (form.value_estimate_cents / 100).toString()
                : ''
            }
            onChange={(e) => {
              const v = e.target.value;
              update(
                'value_estimate_cents',
                v === '' ? null : Math.round(parseFloat(v) * 100),
              );
            }}
          />
        </Field>
        <Field label="Tags" className="col-span-2">
          <TagsInput value={tags} onChange={setTags} />
        </Field>
      </div>
      {isLost ? (
        <Field label="Lost reason">
          <Input
            value={form.lost_reason ?? ''}
            onChange={(e) => update('lost_reason', e.target.value)}
            placeholder="Price, timing, no fit…"
          />
        </Field>
      ) : null}
      <Field label="Notes">
        <Textarea
          value={form.notes ?? ''}
          onChange={(e) => update('notes', e.target.value)}
          rows={4}
        />
      </Field>
      {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block space-y-1.5 ${className ?? ''}`}>
      <span className="text-xs font-medium text-[var(--color-foreground-muted)]">{label}</span>
      {children}
    </label>
  );
}
